import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * The left navigation's two independent collapse states (backlog item 1).
 *
 * `collapsed` is the whole sidebar folding down to an icon rail; `closed` is
 * the set of groups — Workspace, Deployment, Admin, Help — the reader has folded
 * shut. Both are persisted for the same reason the workspace split is: someone
 * who reclaims that column means it for the next page too, and a nav that
 * springs back open on every route change is a nav that is arguing.
 *
 * Groups are stored as a list of headings rather than a Set because this goes
 * through JSON on its way to localStorage.
 */

interface SidebarState {
  /** Icon-rail mode: the whole column folded down to its glyphs. */
  collapsed: boolean;
  /** Headings the reader has folded shut. */
  closed: string[];
  toggle: () => void;
  setCollapsed: (collapsed: boolean) => void;
  toggleGroup: (heading: string) => void;
}

export const useSidebar = create<SidebarState>()(
  persist(
    (set) => ({
      collapsed: false,
      // Admin ships folded: five rows most readers open once a month, and it
      // will be access-restricted anyway (PRD P2). The accent dot on a folded
      // heading covers the case where the current page is inside it.
      closed: ["Admin"],
      toggle: () => set((state) => ({ collapsed: !state.collapsed })),
      setCollapsed: (collapsed) => set({ collapsed }),
      toggleGroup: (heading) =>
        set((state) => ({
          closed: state.closed.includes(heading)
            ? state.closed.filter((entry) => entry !== heading)
            : [...state.closed, heading],
        })),
    }),
    {
      name: "ming.sidebar",
      partialize: (state) => ({ collapsed: state.collapsed, closed: state.closed }),
      // Stored state wins over the defaults above, so a default change alone
      // would never reach anyone who has already used the app. The Admin group
      // did not exist before v1, which is why applying it once here overwrites
      // no deliberate choice.
      version: 1,
      migrate: (persisted, from) => {
        const state = persisted as { collapsed?: boolean; closed?: string[] } | undefined;
        if (from >= 1) return state;
        return { ...state, closed: ["Admin"] };
      },
    },
  ),
);
